import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
// hooks
import useDialog from "hooks/useDialog";
import { useAppDispatch } from "hooks/redux";
// models
import IPropertyOwnership from "models/PropertyOwnership";
// types
import PropertyOwnershipStatus from "types/PropertyOwnershipStatus";
// store
import PropertyOwnershipsAsync from "store/propertyOwnerships/propertyOwnershipsAsync";
import { selectIsMyPropertyOwnerships, selectMyPropertyOwnershipsTotal, selectMyPropertyOwnershipsParams } from "store/propertyOwnerships/propertyOwnershipsSelectors";
// components
import MyPropertyOwnershipCard from "./MyPropertyOwnershipCard";
import Title from "components/Title";
import { Loader, NoData } from "components/Utilities";
import PropertyOwnershipsForm from "./MyPropertyOwnerships.form";
// mui
import { Box, Button, Tab, TablePagination, Tabs } from "@mui/material";
import { makeStyles } from '@mui/styles';
// Utilities
import { checkPage, getContent } from "utilities/Utilities";

const MyPropertyOwnerships:React.FC = () => {
  const dispatch = useAppDispatch();
  const classes = useStyles();

  const propertyOwnerships:IPropertyOwnership[] | null = useSelector(selectIsMyPropertyOwnerships) || [];
  const total:number = useSelector(selectMyPropertyOwnershipsTotal);
  const params:any = useSelector(selectMyPropertyOwnershipsParams);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(20);

  const [tab, setTab] = useState<PropertyOwnershipStatus>(params.statuses[0]);

  const handleChangeTab = (_: any, newTab: PropertyOwnershipStatus) => {
    setTab(newTab);
  };

  const handleChangePage = (_:any, nextPage:number) => {
    setIsLoading(true);
    setPage(nextPage);
    dispatch(PropertyOwnershipsAsync.fetchMyPropertyOwnerships({ size, page: nextPage, statuses: [tab] }))
      .unwrap()
      .finally(() => setIsLoading(false));
  }

  const handleChangeLimit = (event: any) => {
    setIsLoading(true);
    const { value:size } = event.target;
    setSize(size);
    setPage(0);
    dispatch(PropertyOwnershipsAsync.fetchMyPropertyOwnerships({ size, page: 0, statuses: [tab] }))
      .unwrap()
      .finally(() => setIsLoading(false));
  }

  useEffect(() => {
    setIsLoading(true);
    dispatch(PropertyOwnershipsAsync.fetchMyPropertyOwnerships({ statuses: [tab], size }))
      .unwrap()
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line
  }, [tab]);
  
  useEffect(() => {
    if (propertyOwnerships && !propertyOwnerships.length && !!total) {
      const newPage = checkPage(page, size, total);
      setPage(newPage);
      setIsLoading(true);
      dispatch(PropertyOwnershipsAsync.fetchMyPropertyOwnerships({...params, page: newPage }))
        .unwrap()
        .finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line
  }, [propertyOwnerships]);

  const { Dialog, openDialog, closeDialog } = useDialog();

  return (
    <React.Fragment>
      <Dialog maxWidth="sm">
        <PropertyOwnershipsForm onClose={closeDialog} />
      </Dialog>

      <Box className={classes.page}>
        <div className="container">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'  }}>
            <Title>{getContent('labels').labelTimesharePluralText}</Title>
            <Button
              variant="contained"
              size="small"
              onClick={openDialog}
              sx={{ display: { sm: 'none' } }}
            >
              {`Create ${getContent('labels').labelTimeshareSingularText}`}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2  }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', maxWidth: 'max-content' }}>
              <Tabs
                variant="scrollable"
                value={tab}
                onChange={handleChangeTab}
                aria-label="property ownerships status tabs"
              >
                {Object.keys(PropertyOwnershipStatus).map((key: string) => (
                  <Tab sx={{ fontSize: { xs: '12px', sm: '14px' } }} key={key} value={PropertyOwnershipStatus[key as keyof typeof PropertyOwnershipStatus]} label={key} />
                ))}
              </Tabs>
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={openDialog}
              sx={{ mb: 2, display: { xs: 'none', sm: 'inline-flex' } }}
            >
              {`Create ${getContent('labels').labelTimeshareSingularText}`}
            </Button>
          </Box>
          {isLoading ? <Loader /> : (
            !!total ? (
              <Box className={classes.list} sx={{ mt: 2 }}>
                <Box className={classes.items}>
                  {propertyOwnerships?.map(propertyOwnership => (
                    <MyPropertyOwnershipCard key={propertyOwnership.id} propertyOwnership={propertyOwnership} />
                  ))}
                </Box>
                <Box className={classes.pagination}>
                  <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={size}
                    onRowsPerPageChange={handleChangeLimit}
                    labelRowsPerPage="Items per page"
                    rowsPerPageOptions={[20, 50, 100]}
                  />
                </Box>
              </Box>
            ) : <NoData />
          )}
        </div>
      </Box>
    </React.Fragment>
  );
}

export default MyPropertyOwnerships;

const useStyles = makeStyles({
  page: {
    height: 'calc(100vh - 266px)',
    backgroundColor: '#F5F6F7',
    padding: '16px',
    '@media (min-width: 600px)': {
      padding: '32px',
    },
    '@media (min-width: 1240px)': {
      padding: '40px 0',
      height: 'calc(100vh - 236px)',
    },
  },
  list: {
    backgroundColor: '#fff',
    borderRadius: '4px',
    boxShadow: '0px 0px 0px 1px #E0E0E0',
    overflow: 'hidden',
  },
  items: {
    maxHeight: 'calc(100vh - 463px)',
    overflowY: 'scroll',
    backgroundColor: '#eee',
    '@media (min-width: 600px)': {
      maxHeight: 'calc(100vh - 495px)',
    },
  },
  pagination: {
    boxShadow: '0px 0px 0px 1px #E0E0E0'
  },
});
